import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { ModalService } from '@src/shared/modal/modal.service';
import { ExtraProductFormatField, ProductFormatService } from '../../../../woo_services.module/ProductFormatService';
import { ExtraPublisherField, PublisherService } from '../../../../woo_services.module/PublisherService';
import {
  PerIdEstimates,
  Publisher,
  SlotType,
  SlotTypeEstimates,
  wooId,
} from '../../../../woo_services.module/shared-types';
import { TranslationService } from '../../../../woo_services.module/TranslationService';
import { ProductsPageUtilityService } from '../../shared/products-page-utility-service';

@Component({
  selector: 'manage-completion-rates-estimates',
  templateUrl: './manage-completion-rates-estimates.component.html',
  styleUrls: ['../../shared/table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ManageCompletionRatesEstimates implements OnInit {
  @ViewChild('stickyHeader', { static: true }) stickyHeader: ElementRef;
  @ViewChild('stickyTableHeader', { static: true }) stickyTableHeader: ElementRef;
  show = {
    estimatesHaveChanged: false,
    updatingEstimates: false,
    isEditing: false,
    loading: true,
  };
  readonly tableHeaders = ['Placering', 'Preroll', 'Midroll', 'Postroll', 'Preroll Sticky', 'Postroll Sticky'];
  readonly staticFields = 1;

  publishers = signal<Publisher[]>([]);
  selectedPublisher = signal<Publisher>(null);
  completionRateRows: CompletionRateRow[] = [];
  includedPublisherSlotTypes: Record<wooId, Record<SlotType, boolean>> = {};
  estimates = null as PerIdEstimates;
  slotTypes = [SlotType.preroll, SlotType.midroll, SlotType.postroll, SlotType.prerollSticky, SlotType.postrollSticky];

  @Input('headerPos') parentHeader: HTMLElement;
  constructor(
    private publisherService: PublisherService,
    private productFormatService: ProductFormatService,
    private utilityService: ProductsPageUtilityService,
    private changeDetection: ChangeDetectorRef,
    private modalService: ModalService,
    private translationService: TranslationService,
  ) {}

  ngOnInit(): void {
    this.getAndBuildCompletionRates()
      .then(() => {
        this.productFormatService
          .getProductFormats(ExtraProductFormatField.slotTypes, ExtraProductFormatField.enabled)
          .then((productFormats) => {
            this.includedPublisherSlotTypes = this.utilityService.setIncludedPublisherSlotTypes(
              productFormats.filter((f) => f.enabled),
            );
          });
      })
      .finally(() => {
        this.show.loading = false;
        this.utilityService.stickifyHeaders(this.stickyHeader, this.stickyTableHeader);
        this.changeDetection.markForCheck();
      });
  }

  async getAndBuildCompletionRates(): Promise<void> {
    return this.publisherService
      .getPublishers(ExtraPublisherField.enabled, ExtraPublisherField.completionRates)
      .then((publishers) => {
        this.publishers.set(publishers.filter((p) => p.enabled));
        this.buildCompletionRateRows();
      });
  }

  setEditing(value: boolean): void {
    this.show.isEditing = value;
  }

  cancelEditing(): void {
    this.getAndBuildCompletionRates();
    this.setEditing(false);
  }

  async save(): Promise<void> {
    const filterActive = this.selectedPublisher();
    const textBlocks = ['Är du säker på att du vill spara dina ändringar?'];
    if (filterActive) {
      textBlocks.push('Notera att även ändringar i filtrerade publicister sparas.');
    }
    this.modalService
      .openConfirm({
        title: 'Bekräfta ändringar',
        texts: textBlocks,
        confirmLabel: 'Spara',
        cancelLabel: 'Avbryt',
      })
      .then((result) => {
        if (result.confirm) {
          this.modalService.closeAll();
          const loaderModal = this.modalService.openLoader('Sparar ändringar');
          const update = this.publishers().map((publisher) => {
            const match = this.completionRateRows.find((row) => row.publisherId === publisher.id);

            return {
              ...publisher,
              completion_rates: {
                midroll: match?.midroll ?? null,
                preroll: match?.preroll ?? null,
              },
            };
          });
          this.show.updatingEstimates = true;
          this.show.loading = true;
          this.publisherService
            .updatePublishers(update)
            .then(() => {
              this.getAndBuildCompletionRates();
              this.show.estimatesHaveChanged = true;
            })
            .catch((err) => {
              this.getAndBuildCompletionRates();
              this.modalService.openError(this.translationService.convertError(err.error.error));
            })
            .finally(() => {
              this.show.updatingEstimates = false;
              this.show.loading = false;
              this.setEditing(false);
              loaderModal.close();
              this.changeDetection.markForCheck();
            });
        }
      })
      .finally(() => {
        this.changeDetection.markForCheck();
      });
  }

  rowVisible(obj: CompletionRateRow): boolean {
    return !this.selectedPublisher() || !(obj.publisherId !== this.selectedPublisher().id);
  }

  getPublisherCompletionRate(publisherId: wooId): SlotTypeEstimates {
    return this.publishers().find((publisher) => publisher.id === publisherId).completion_rates;
  }

  private buildCompletionRateRows(): void {
    let completionRateRows: CompletionRateRow[] = [];

    this.publishers().map((publisher) => {
      completionRateRows = [
        ...completionRateRows,
        {
          id: publisher.id,
          publisher: publisher.name,
          publisherId: publisher.id,
          preroll: publisher.completion_rates.preroll,
          midroll: publisher.completion_rates.midroll,
          postroll: publisher.completion_rates.postroll,
          prerollSticky: publisher.completion_rates.preroll_sticky,
          postrollSticky: publisher.completion_rates.postroll_sticky,
        },
      ];
    });
    this.completionRateRows = completionRateRows;
  }
}

interface CompletionRateRow {
  id: string;
  publisher: string;
  publisherId?: wooId;
  preroll?: number;
  midroll?: number;
  postroll?: number;
  prerollSticky?: number;
  postrollSticky?: number;
}
